/**
 * useCivicInteractions Hook
 *
 * Central hook for tracking all user interactions across Civicbeat.
 * Writes to Supabase tables and updates daily activity automatically.
 *
 * Key Features:
 * - Tracks all story interactions (opens, timeline views, backstory, micro-actions)
 * - Records emotional reactions (feelings)
 * - Manages saved items (songs, playlists, stories)
 * - Handles submissions (songs, feedback)
 * - Updates user interests
 * - Automatically increments daily activity and profile counters
 * - Resilient error handling (logs errors but doesn't crash UI)
 */

import { useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { trackPassiveEngagement, trackActiveEngagement } from '../lib/civicLevelService';
import type {
  CivicInteractions,
  TrackStoryPayload,
  TrackMicroActionPayload,
  TrackFeelingPayload,
  SaveStoryPayload,
  SavePlaylistPayload,
  SubmitSongPayload,
  SubmitFeedbackPayload,
  InteractionType,
  FeelingType,
} from '../lib/civicTypes';

export function useCivicInteractions(): CivicInteractions {
  const { user } = useAuth();
  const [isTracking, setIsTracking] = useState(false);

  /**
   * Core tracking function - handles all writes to Supabase
   * 1. Writes interaction to user_interactions
   * 2. Updates daily activity (upserts today's row)
   * 3. Increments profile total_engagements counter
   */
  const trackInteraction = async (
    type: InteractionType,
    payload: {
      storyId?: string;
      playlistId?: string;
      issueTags?: string[];
      meta?: Record<string, any>;
    }
  ): Promise<void> => {
    if (!user) {
      console.log('[trackInteraction] No user logged in, skipping track');
      return;
    }

    try {
      setIsTracking(true);
      console.log('[trackInteraction] START -', type, payload);

      // 1. Insert interaction record
      const { error: interactionError } = await supabase
        .from('user_interactions')
        .insert({
          user_id: user.id,
          story_id: payload.storyId || null,
          playlist_id: payload.playlistId || null,
          type,
          issue_tags: payload.issueTags || null,
          meta: payload.meta || null,
        });

      if (interactionError) {
        console.error('[trackInteraction] Error inserting interaction:', interactionError);
        return;
      }

      console.log('[trackInteraction] Interaction inserted successfully');

      // 2. Update daily activity using user's local date
      const today = new Date();
      const localDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      console.log('[trackInteraction] Incrementing daily activity for date:', localDate);

      const { error: activityError } = await supabase.rpc('increment_daily_activity', {
        p_user_id: user.id,
        p_date: localDate,
      });

      if (activityError) {
        console.error('[trackInteraction] Error incrementing daily activity:', activityError);
      } else {
        console.log('[trackInteraction] Daily activity incremented successfully');
      }

      // 3. Increment profile counters
      console.log('[trackInteraction] Incrementing profile engagement...');

      const { error: profileError } = await supabase.rpc('increment_profile_engagement', {
        p_user_id: user.id,
      });

      if (profileError) {
        console.error('[trackInteraction] Error incrementing profile engagement:', profileError);
      } else {
        console.log('[trackInteraction] Profile engagement incremented successfully');
      }

      console.log('[trackInteraction] COMPLETE -', type);
    } catch (error) {
      console.error('[trackInteraction] Unexpected error:', error);
    } finally {
      setIsTracking(false);
    }
  };

  /**
   * Track when a user opens a story card
   * Awards 1 CP (capped at 5 CP/day total passive)
   * Only awards CP first time per story per day
   */
  const trackStoryOpened = async (payload: TrackStoryPayload): Promise<void> => {
    // CRITICAL: Award CP BEFORE logging interaction
    // The CP function checks user_interactions to prevent duplicates,
    // so we must call it before the interaction is logged
    if (user) {
      await trackPassiveEngagement(user.id, 'story_opened', 1, payload.storyId);
    }

    // Log interaction for analytics (happens after CP award to avoid false duplicate detection)
    await trackInteraction('open_story_card', {
      storyId: payload.storyId,
      issueTags: payload.issueTags,
    });
  };

  /**
   * Track when a user opens the timeline view
   * NO CP AWARDED - analytics only
   */
  const trackTimelineOpened = async (payload: TrackStoryPayload): Promise<void> => {
    await trackInteraction('timeline_opened', {
      storyId: payload.storyId,
      issueTags: payload.issueTags,
    });
  };

  /**
   * Track when a user opens the backstory section
   * NO CP AWARDED - analytics only
   */
  const trackBackstoryOpened = async (payload: TrackStoryPayload): Promise<void> => {
    await trackInteraction('backstory_opened', {
      storyId: payload.storyId,
      issueTags: payload.issueTags,
    });
  };

  /**
   * Track when a user takes a micro-action (show roots, etc.)
   */
  const trackMicroAction = async (payload: TrackMicroActionPayload): Promise<void> => {
    await trackInteraction('micro_action_taken', {
      storyId: payload.storyId,
      issueTags: payload.issueTags,
      meta: { microAction: payload.microActionKey },
    });
  };

  /**
   * Track emotional reaction to a story
   * Writes to both user_feelings and user_interactions
   */
  const trackFeeling = async (payload: TrackFeelingPayload): Promise<void> => {
    if (!user) return;

    try {
      // Write to user_feelings table
      const { error: feelingError } = await supabase.from('user_feelings').insert({
        user_id: user.id,
        story_id: payload.storyId,
        feeling_type: payload.feelingType,
        issue_tags: payload.issueTags || null,
      });

      if (feelingError) {
        console.error('[useCivicInteractions] Error tracking feeling:', feelingError);
        return;
      }

      // Also track as an interaction for engagement counting
      await trackInteraction('emotion_clicked', {
        storyId: payload.storyId,
        issueTags: payload.issueTags,
        meta: { feeling: payload.feelingType },
      });

      console.log(`[useCivicInteractions] Tracked feeling: ${payload.feelingType}`);
    } catch (error) {
      console.error('[useCivicInteractions] Error in trackFeeling:', error);
    }
  };

  /**
   * Save a story to user's saved items
   * Awards 3 CP (active cap, once per story per day)
   */
  const saveStory = async (payload: SaveStoryPayload): Promise<void> => {
    if (!user) return;

    try {
      const { error } = await supabase.from('user_saved_items').insert({
        user_id: user.id,
        item_type: 'tile',
        item_id: payload.storyId,
        issue_tags: payload.issueTags || null,
      });

      if (error) {
        console.error('[useCivicInteractions] Error saving story:', error);
        return;
      }

      // Track as interaction
      await trackInteraction('save_story', {
        storyId: payload.storyId,
        issueTags: payload.issueTags,
      });

      // Award active CP
      await trackActiveEngagement(user.id, payload.storyId, 'save_story');

      console.log('[useCivicInteractions] Story saved:', payload.storyId);
    } catch (error) {
      console.error('[useCivicInteractions] Error in saveStory:', error);
    }
  };

  /**
   * Remove a story from saved items
   */
  const unsaveStory = async (storyId: string): Promise<void> => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_saved_items')
        .delete()
        .eq('user_id', user.id)
        .eq('item_type', 'tile')
        .eq('item_id', storyId);

      if (error) {
        console.error('[useCivicInteractions] Error unsaving story:', error);
        return;
      }

      console.log('[useCivicInteractions] Story unsaved:', storyId);
    } catch (error) {
      console.error('[useCivicInteractions] Error in unsaveStory:', error);
    }
  };

  /**
   * Save a playlist
   * NO CP AWARDED - analytics only
   */
  const savePlaylist = async (payload: SavePlaylistPayload): Promise<void> => {
    if (!user) return;

    try {
      const { error } = await supabase.from('user_saved_items').insert({
        user_id: user.id,
        item_type: 'playlist',
        item_id: payload.playlistId,
        issue_tags: payload.issueTags || null,
      });

      if (error) {
        console.error('[useCivicInteractions] Error saving playlist:', error);
        return;
      }

      // Track as interaction
      await trackInteraction('playlist_saved', {
        playlistId: payload.playlistId,
        issueTags: payload.issueTags,
      });

      console.log('[useCivicInteractions] Playlist saved:', payload.playlistId);
    } catch (error) {
      console.error('[useCivicInteractions] Error in savePlaylist:', error);
    }
  };

  /**
   * Remove a playlist from saved items
   */
  const unsavePlaylist = async (playlistId: string): Promise<void> => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_saved_items')
        .delete()
        .eq('user_id', user.id)
        .eq('item_type', 'playlist')
        .eq('item_id', playlistId);

      if (error) {
        console.error('[useCivicInteractions] Error unsaving playlist:', error);
        return;
      }

      console.log('[useCivicInteractions] Playlist unsaved:', playlistId);
    } catch (error) {
      console.error('[useCivicInteractions] Error in unsavePlaylist:', error);
    }
  };

  /**
   * Submit a new song (already has its own table)
   */
  const submitSong = async (payload: SubmitSongPayload): Promise<void> => {
    if (!user) return;

    try {
      // Song submissions go to existing song_submissions table
      const { error } = await supabase.from('song_submissions').insert({
        user_id: user.id,
        artist_name: payload.artist,
        song_title: payload.title,
        release_year: payload.year,
        issue_focus: payload.issue,
        why_civicbeat: payload.why,
        user_email: payload.email,
      });

      if (error) {
        console.error('[useCivicInteractions] Error submitting song:', error);
        throw error;
      }

      // Track as interaction
      await trackInteraction('song_submitted', {
        meta: { artist: payload.artist, title: payload.title },
      });

      console.log('[useCivicInteractions] Song submitted');
    } catch (error) {
      console.error('[useCivicInteractions] Error in submitSong:', error);
      throw error;
    }
  };

  /**
   * Submit feedback (already has its own table)
   */
  const submitFeedback = async (payload: SubmitFeedbackPayload): Promise<void> => {
    if (!user) return;

    try {
      // Feedback goes to existing feedback_submissions table
      const { error } = await supabase.from('feedback_submissions').insert({
        user_id: user.id,
        feedback_type: payload.type,
        message: payload.message,
        user_email: payload.email,
      });

      if (error) {
        console.error('[useCivicInteractions] Error submitting feedback:', error);
        throw error;
      }

      // Track as interaction
      await trackInteraction('feedback_submitted', {
        meta: { type: payload.type },
      });

      console.log('[useCivicInteractions] Feedback submitted');
    } catch (error) {
      console.error('[useCivicInteractions] Error in submitFeedback:', error);
      throw error;
    }
  };

  /**
   * Update user's issue interests
   * Replaces all existing interests with new set
   */
  const updateInterests = async (issueTags: string[]): Promise<void> => {
    if (!user) return;

    try {
      // Delete existing interests
      await supabase.from('user_interests').delete().eq('user_id', user.id);

      // Insert new interests
      if (issueTags.length > 0) {
        const interests = issueTags.map((tag) => ({
          user_id: user.id,
          issue_tag: tag,
        }));

        const { error } = await supabase.from('user_interests').insert(interests);

        if (error) {
          console.error('[useCivicInteractions] Error updating interests:', error);
          return;
        }
      }

      // Track as interaction
      await trackInteraction('interest_updated', {
        meta: { interests: issueTags },
      });

      console.log('[useCivicInteractions] Interests updated:', issueTags);
    } catch (error) {
      console.error('[useCivicInteractions] Error in updateInterests:', error);
    }
  };

  /**
   * Track identity reaction (resonates, worried, curious)
   * Stores in user_interactions for profile metrics
   */
  const trackIdentityReaction = async (payload: {
    storyId: string;
    reaction: 'resonates' | 'worried' | 'curious';
    issueTags: string[];
  }): Promise<void> => {
    await trackInteraction('identity_reaction', {
      storyId: payload.storyId,
      issueTags: payload.issueTags,
      meta: { reaction: payload.reaction },
    });
  };

  /**
   * Track when a user clicks "Listen Now" (Spotify, Apple Music, YouTube)
   * Awards 2 CP (active cap, once per story per day)
   */
  const trackListenNowClick = async (storyId: string): Promise<void> => {
    if (!user) {
      console.log('[trackListenNowClick] ❌ No user, skipping');
      return;
    }

    console.log('=== [trackListenNowClick] START ===');
    console.log('[trackListenNowClick] User ID:', user.id);
    console.log('[trackListenNowClick] Story ID:', storyId);

    try {
      // Award active CP (track_active_engagement handles the user_interactions insert)
      console.log('[trackListenNowClick] Calling trackActiveEngagement...');
      const result = await trackActiveEngagement(user.id, storyId, 'listen_now_clicked');
      console.log('[trackListenNowClick] ✅ trackActiveEngagement complete');
      console.log('[trackListenNowClick] CP Awarded:', result?.cpAwarded);
      console.log('[trackListenNowClick] New Total XP:', result?.newTotalXP);
      console.log('[trackListenNowClick] Full Result:', result);

      console.log('=== [trackListenNowClick] SUCCESS ===');
    } catch (error) {
      console.error('=== [trackListenNowClick] ERROR ===', error);
    }
  };

  /**
   * Track when a user selects an emotion reaction
   * Awards 1 CP (max 3 CP per day total for all emotions)
   * Also writes to user_feelings table for emotional tracking
   */
  const trackEmotionClick = async (storyId: string, emotion: string, issueTags?: string[]): Promise<void> => {
    if (!user) {
      console.log('[trackEmotionClick] No user, skipping');
      return;
    }

    try {
      console.log('[trackEmotionClick] Tracking emotion click:', emotion, 'for song:', storyId);

      // 1. Write to user_feelings table first
      const feelingTypeMap: Record<string, string> = {
        'Moved': 'moved',
        'Empowered': 'empowered',
        'Curious': 'curious',
        'Heavy': 'heavy',
      };

      const { error: feelingError } = await supabase.from('user_feelings').insert({
        user_id: user.id,
        story_id: storyId,
        feeling_type: feelingTypeMap[emotion] || emotion.toLowerCase(),
        issue_tags: issueTags || null,
      });

      if (feelingError) {
        console.error('[trackEmotionClick] Error writing to user_feelings:', feelingError);
      }

      // 2. Track as interaction for analytics
      await trackInteraction('emotion_clicked', {
        storyId,
        issueTags,
        meta: { emotion },
      });

      // 3. Award active CP (special handling for emotion cap)
      await trackActiveEngagement(user.id, storyId, 'emotion_clicked');

      console.log('[trackEmotionClick] Emotion click tracked successfully');
    } catch (error) {
      console.error('[trackEmotionClick] ERROR:', error);
    }
  };

  return {
    trackStoryOpened,
    trackTimelineOpened,
    trackBackstoryOpened,
    trackMicroAction,
    trackFeeling,
    trackIdentityReaction,
    trackListenNowClick,
    trackEmotionClick,
    saveStory,
    unsaveStory,
    savePlaylist,
    unsavePlaylist,
    submitSong,
    submitFeedback,
    updateInterests,
    isTracking,
  };
}
